import { ModalFuncProps } from "antd/lib/modal";
import { Business } from "../../../models/Business";
import React from "react";
import { Col, Row, Alert, Form, Checkbox } from "antd";
import { formatDate } from "../../../util";
import Text from "antd/lib/typography/Text";
import Paragraph from "antd/lib/typography/Paragraph";
import { CustomerObjectMapType } from "../../../util/providers/CustomerProvider";
import { FormInstance } from "antd/lib/form";
import { Store } from "antd/lib/form/interface";
import Stripe from "stripe";

export type CanNotCancelProps = {
  cancelling?: boolean;
  business?: Business;
};

/**
 * Creates the props for the modal when the subscription can not be
 * cancelled
 *
 */
export const CanNotCancelModalProps: ({
  cancelling,
  business,
}: CanNotCancelProps) => ModalFuncProps = ({ cancelling, business }) => {
  return {
    title: "Already cancelled",
    content: (
      <div>
        <>
          <Row gutter={[0, 16]}>
            <Col>
              {cancelling && (
                <>
                  <Paragraph>
                    This subscription is already set to be cancelled on{" "}
                    <Text strong>
                      {formatDate(
                        business?.subscription?.current_period_end
                          ? business?.subscription?.current_period_end * 1000
                          : 0
                      )}
                    </Text>
                    .
                  </Paragraph>
                </>
              )}
            </Col>
          </Row>
        </>
      </div>
    ),
  };
};

export type ConfirmCancelProps = {
  business: Business;
  customer: CustomerObjectMapType;
  handleSubscriptionCancel: (values: Store) => void;
  form: FormInstance;
};
/**
 * Creates the props for the modal when the subscription is about to be cancelled.
 * This will prompt the user for confirmation before calling the function passed in via
 * the component props.
 *
 * @param business subscription belongs to this business
 * @param action if the subscription is in a schedule set this to "release", set to "cancel"
 */
export const ConfirmCancelModalProps: ({
  form,
  business,
  customer,
  handleSubscriptionCancel,
}: ConfirmCancelProps) => ModalFuncProps = ({
  form,
  business,
  customer,
  handleSubscriptionCancel,
}) => {
  const { subscription } = business;

  const price = subscription?.items.data[0].price;
  const product = price?.product as Stripe.Product;
  const isInvoiced =
    product?.metadata.gift_eligible === "1" || price?.metadata.trade === "true";

  return {
    title: "Cancel subscription",
    content: (
      <div>
        <Form form={form} onFinish={handleSubscriptionCancel}>
          <Row gutter={[0, 16]}>
            <Col>
              <Alert
                type="warning"
                message={
                  <div>
                    <Text strong>This action is irreversible.</Text>
                    <Paragraph>
                      If you believe you have made a mistake contact iLivestock.
                    </Paragraph>
                  </div>
                }
              />
            </Col>
          </Row>
          <Row gutter={[0, 16]}>
            <Col>
              <Paragraph>
                You are about to cancel your iLivestock plan for{" "}
                <strong>
                  {customer?.givenName && customer.familyName
                    ? `${customer?.givenName} ${customer?.familyName}`
                    : customer?.email
                    ? customer.email
                    : "Unknown customer"}
                </strong>
                . Are you sure you want to continue?
              </Paragraph>
            </Col>
          </Row>
          <Row gutter={[0, 16]}>
            <Col>
              {isInvoiced ? (
                <Paragraph>
                  The invoice will be <strong>cancelled immediately</strong>.
                  The customer will no longer be able to use the product
                </Paragraph>
              ) : (
                <Paragraph>
                  The customer will be able to continue using iLivestock within
                  the limits of the plan until{" "}
                  <Text strong>
                    {formatDate(
                      subscription?.current_period_end
                        ? subscription.current_period_end * 1000
                        : 0
                    )}
                  </Text>
                </Paragraph>
              )}
            </Col>
          </Row>
          <Row gutter={[0, 16]}>
            <Col>
              <Paragraph>
                If you are cancelling an active subscription that was supposed
                to be cancelled during its trial, please check this checkbox
              </Paragraph>
              <Form.Item
                name="lateCancellation"
                valuePropName="checked"
                noStyle
              >
                <Checkbox value="false">
                  I declare this cancellation should not be chargeable.
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    ),
    onOk: form.submit,
  };
};
